// ABOUTME: Presents the Play landing page from the selected 04 concept board.
// ABOUTME: The hero field carries a private draft description into the account workflow.
import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { localNumber, type PublicConfig } from "../api";
import { BrandLogo } from "./BrandLogo";
import { PublicFooter, PublicHeader } from "./PublicPages";
import { Button } from "./ui/controls";
import {
  Bookmark,
  Check,
  Globe2,
  Monitor,
  Plus,
  ShieldCheck,
  Volume2,
  VolumeX,
} from "./ui/icons";
import "../styles/landing.css";

const previewNames = ["Avenlo", "Mivora", "Soluno"];
const previewChecks = [
  { Icon: Globe2, label: "Check domains" },
  { Icon: Monitor, label: "Web matches" },
  { Icon: ShieldCheck, label: "Trademark coverage" },
];
const steps = [
  { title: "Describe", text: "Tell us what you’re building." },
  { title: "Explore", text: "Get name ideas that fit." },
  { title: "Research", text: "Check domains, web and trademarks." },
  { title: "Shortlist", text: "Keep the ones that click." },
];

export function Landing({
  navigate,
  config,
}: {
  navigate: (path: string) => void;
  config: PublicConfig | null;
}) {
  const [description, setDescription] = useState("");
  function start(event: FormEvent) {
    event.preventDefault();
    if (description.trim())
      sessionStorage.setItem("novaname.description", description.trim());
    navigate("/login");
  }
  const generation = config?.allowances?.generation;
  const checks = config?.allowances?.new_checks;
  return (
    <div className="public-page landing">
      <PublicHeader navigate={navigate} />
      <main>
        <section className="landing-hero" aria-labelledby="hero-title">
          <div className="landing-hero-copy">
            <h1 id="hero-title">
              Find your <span>next name.</span>
            </h1>
            <p>Describe your business and get name ideas with real research.</p>
            <form className="landing-form" onSubmit={start}>
              <label className="sr-only" htmlFor="business-intro">
                Describe your business
              </label>
              <input
                id="business-intro"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={4000}
                placeholder="Describe your business"
              />
              <Button type="submit">Start naming</Button>
            </form>
          </div>
          <div className="landing-panel" aria-hidden="true">
            <p>
              Ideas
              <br />
              Checks
              <br />
              Shortlists
              <br />
              Brighter
              <br />
              Tomorrows
            </p>
          </div>
        </section>

        <section
          className="landing-product"
          id="product"
          aria-labelledby="product-title"
        >
          <h2 id="product-title" className="sr-only">
            Product
          </h2>
          <figure
            className="landing-preview"
            role="img"
            aria-label="A NovaName shortlist comparing Avenlo, Mivora and Soluno"
          >
            <div className="landing-preview-bar" aria-hidden="true">
              <BrandLogo />
              <span>New search</span>
              <span>Saved</span>
              <span>Account</span>
            </div>
            <div className="landing-preview-body" aria-hidden="true">
              <div className="landing-preview-heading">
                <div>
                  <strong>Your shortlist</strong>
                  <span>Compare names and run the checks that matter.</span>
                </div>
                <span className="landing-preview-button">
                  <Plus /> New search
                </span>
              </div>
              <ul>
                {previewNames.map((name) => (
                  <li key={name}>
                    <strong>{name}</strong>
                    {previewChecks.map(({ Icon, label }) => (
                      <span key={label} className="landing-preview-check">
                        <Icon />
                        {label}
                      </span>
                    ))}
                    <span className="landing-preview-save">
                      <Bookmark />
                      Save
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </figure>
        </section>

        <section
          className="landing-steps"
          id="how-it-works"
          aria-labelledby="steps-title"
        >
          <h2 id="steps-title" className="sr-only">
            How it works
          </h2>
          <DemoVideo />
          <ol>
            {steps.map((step) => (
              <li key={step.title}>
                <span>{step.title}</span>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="landing-pricing"
          id="pricing"
          aria-labelledby="pricing-title"
        >
          <div>
            <h2 id="pricing-title">Free to start.</h2>
            <p>Rechecks use your monthly checks too.</p>
          </div>
          <ul>
            <li>
              <Check />
              {generation === undefined
                ? "New names every month"
                : `${localNumber(generation)} new names a month`}
            </li>
            <li>
              <Check />
              {checks === undefined
                ? "Name checks every month"
                : `${localNumber(checks)} name checks a month`}
            </li>
            <li>
              <Check />
              Existing results, free
            </li>
          </ul>
          <Button onClick={() => navigate("/login")}>Get started</Button>
        </section>

        <section className="landing-close" aria-labelledby="close-title">
          <div>
            <h2 id="close-title">
              Find your
              <br />
              next name.
            </h2>
            <span className="landing-domain">novaname.ai</span>
            <Button variant="inverse" onClick={() => navigate("/login")}>
              Get started
            </Button>
          </div>
          <p className="landing-labels">
            <span className="landing-label landing-label-action">
              Better
              <br />
              names
            </span>
            <span className="landing-label landing-label-accent">
              Brighter
              <br />
              futures
            </span>
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

// The product demo (scripts/demo-video): plays muted on a loop; the sound button restarts it with sound.
// With reduced motion it waits on its poster with the native controls instead.
function DemoVideo() {
  const video = useRef<HTMLVideoElement>(null);
  const [sound, setSound] = useState(false);
  const [reducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  function toggleSound() {
    const player = video.current;
    if (!player) return;
    player.muted = sound;
    setSound(!sound);
    if (!sound) {
      player.currentTime = 0;
      void player.play();
    }
  }
  return (
    <figure className="landing-demo">
      <video
        ref={video}
        src="/video/novaname-demo.mp4"
        poster="/video/novaname-demo-poster.webp"
        width={1280}
        height={720}
        autoPlay={!reducedMotion}
        controls={reducedMotion}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label="NovaName demo: a travel brief becomes name ideas with reasons, two are checked, and Dunemoor comes back with nothing found."
      />
      {!reducedMotion && (
        <button
          type="button"
          className="landing-demo-sound"
          aria-pressed={sound}
          aria-label="Play the demo with sound"
          onClick={toggleSound}
        >
          {sound ? (
            <Volume2 aria-hidden="true" />
          ) : (
            <VolumeX aria-hidden="true" />
          )}
        </button>
      )}
    </figure>
  );
}
